import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../redux/store";
import { getEnrolledChannels, joinChannel } from "../../redux/actions/programAction";
import { useHistory, useLocation } from "react-router-dom";
import {
  Button,
  Box,
  Typography,
  InputBase,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Card as MuiCard,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
} from "@material-ui/core";
import { Search, ExpandMore, ExpandLess } from "@material-ui/icons";
import LaunchIcon from "../Icons/LaunchIcon";
import backIcon from "../../../src/assets/icons/backIcon.svg";
import appLogo from "../../../src/assets/logo.png"; // ← rename to avoid confusion
import { useStyles } from "./style";
import { useChannelPost, useIsMobile, useSearchPosts } from "../../util/hooks";
import {
  closeProgramFormModal,
  getProgramFormStructure,
  openProgramFormModal,
} from "../../redux/actions/programFormAction";
import { closeEnrollmentLoading } from "../../redux/actions/programEnrollmentAction";
import { handleDrawer } from "../../redux/actions/drawerAction";
import ActionItemFormModal from "../../screens/OnBoarding/Screens/Components/ActionItemFormModal";
import DOMPurify from "dompurify";
import { assetUrl } from "../../util/assetUrl";
import { usePrivileges } from "../../util/usePrivileges";
import { canPostToChannel } from "../../util/permissions";
import {
  DEFAULT_CHANNEL_THEME,
  sanitizeChannelTheme,
  invertHexColor,
  isHexColorLight,
  ChannelTheme
} from "../../util/theme";
import ChannelBellMenu from "../ChannelBellMenu";
import ChatWindow from "../ChatWindow";
import ChannelChatsView from "./ChannelChatsView";
import { getChannelChatroomsApi } from "../../api/chatrooms";

/* ===== API base (for backend-served /assets or /uploads) ===== */
const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:9090";
/* 🔧 Use ASSET base when serving stored files (matches other screens) */
const ASSET_BASE = process.env.REACT_APP_ASSET_BASE_URL || API_BASE;

/* ===== Resolve any header path to a usable URL =====
   - blob:/data:/absolute http(s) -> return as is
   - /assets or /uploads -> prefix with ASSET_BASE (served by backend)
   - everything else -> run through assetUrl (public or imported assets) */
const resolveHeaderUrl = (p: string): string => {
  if (!p) return "";
  if (/^(blob:|data:|https?:\/\/)/i.test(p)) return p;
  if (p.startsWith("/assets") || p.startsWith("/uploads")) return `${ASSET_BASE}${p}`;
  try {
    return assetUrl(p);
  } catch {
    return p;
  }
};

// helper to decode HTML entities without adding a dependency
function decodeHtml(html: string) {
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
}

function ChannelDetail({ handleBack, channel }: any) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const isMobile = useIsMobile();

  // --- Redux state
  const { programChannelPost, channelDetails, subscribeChannels } = useSelector(
    (state: RootState) => state.programReducer
  );
  const reduxProgram = useSelector((state: RootState) => state.programReducer.singleProgram);
  const { formModal, formStructure } = useSelector(
    (state: RootState) => state.programFormReducer
  );

  // localStorage / auth
  const activeProgramEnrollment = useMemo(
    () => JSON.parse(localStorage.getItem("activeProgramEnrollment") || "{}"),
    []
  );
  const auth = useMemo(() => JSON.parse(localStorage.getItem("authInfo") || "{}"), []);
  const { programId, userId, communityIdentifier } = activeProgramEnrollment || {};
  const { privileges } = usePrivileges(communityIdentifier || undefined);

  // Build an effective channel object (fresh -> channelDetails -> prop)
  const effectiveChannel = useMemo(() => {
    const cd: any = channelDetails || {};
    const propCh: any = channel || {};
    const targetChannelId = cd?.channelIdentifier || propCh?.channelIdentifier;
    const targetCommunityId =
      communityIdentifier || cd?.communityIdentifier || propCh?.communityIdentifier;

    let fresh: any = null;
    if (reduxProgram && targetChannelId && targetCommunityId) {
      const comm = reduxProgram.communities?.find(
        (c: any) => c.communityIdentifier === targetCommunityId
      );
      fresh =
        comm?.channels?.find((ch: any) => ch.channelIdentifier === targetChannelId) ||
        null;
    }

    // precedence: fresh (latest Redux singleProgram) overrides channelDetails, which overrides prop
    // IMPORTANT: fresh must be LAST to ensure channelFormSettings comes from program, not stale enrollment
    return { ...(propCh || {}), ...(channelDetails || {}), ...(fresh || {}) };
  }, [reduxProgram, channelDetails, channel, communityIdentifier]);

  // derive channel identifier from the effective source
  const channelIdentifier = (effectiveChannel as any)?.channelIdentifier;

  // fetch posts for this channel
  useChannelPost(channelIdentifier);

  // pull the live theme for this channel
  const channelTheme = useSelector(
    (state: RootState) => (channelIdentifier ? state.channelThemeReducer[channelIdentifier] : undefined)
  );

  /* ---------- NEW: community style fallback ---------- */
  const communityStyle = useMemo(() => {
    const comm = reduxProgram?.communities?.find(
      (c: any) => c?.communityIdentifier === communityIdentifier
    );
    return (comm as any)?.communityStyle;
  }, [reduxProgram, communityIdentifier]);

  const resolvedTheme: ChannelTheme = useMemo(
    () =>
      sanitizeChannelTheme(
        DEFAULT_CHANNEL_THEME,
        communityStyle,
        (effectiveChannel as any)?.channelStyle,
        channelTheme
      ),
    [communityStyle, effectiveChannel, channelTheme]
  );

  // ensure global theme slice is kept in sync with the latest base style
  useEffect(() => {
    if (!channelIdentifier) return;
    dispatch({
      type: "SET_CHANNEL_THEME",
      channelId: channelIdentifier,
      payload: resolvedTheme,
    });
  }, [
    dispatch,
    channelIdentifier,
    resolvedTheme.mainColor,
    resolvedTheme.secondaryColor,
    resolvedTheme.headerImage,
    resolvedTheme.fontStyle,
  ]);

  const { mainColor, secondaryColor, headerImage, fontStyle } = resolvedTheme;
  const invertedSecondary = useMemo(() => invertHexColor(secondaryColor), [secondaryColor]);

  /* ----------------- Cache-busting additions BEGIN ----------------- */
  const cacheSig = useMemo(() => {
    return [
      headerImage || "",
      (effectiveChannel as any)?.channelStyle?.updatedAt || "",
      (effectiveChannel as any)?.updatedAt || "",
      (reduxProgram as any)?.updatedAt || "",
      (channelTheme as any)?.updatedAt || (channelTheme as any)?.version || ""
    ].join("|");
  }, [headerImage, effectiveChannel, reduxProgram, channelTheme]);

  const [imgNonce, setImgNonce] = useState(0);
  useEffect(() => {
    setImgNonce((n) => n + 1);
  }, [cacheSig]);

  const [openNonce] = useState(() => Date.now());
  /* ------------------ Cache-busting additions END ------------------ */

  // 🔹 Resolve header image to a fetchable URL (works for baked themes & backend assets)
  const headerImageUrl = useMemo(() => {
    const base = resolveHeaderUrl(headerImage || "");
    if (!base) return "";
    const sep = base.includes("?") ? "&" : "?";
    return `${base}${sep}cb=${imgNonce}&nc=${openNonce}`;
  }, [headerImage, imgNonce, openNonce]);

  /* 🔧 COMMUNITY LOGO (same treatment as other pages) */
  const communityLogoRaw = communityStyle?.logo || "";
  const [logoBust, setLogoBust] = useState(0);
  useEffect(() => {
    setLogoBust(Date.now());
  }, [communityLogoRaw, communityIdentifier]);
  const communityLogoUrl = useMemo(() => {
    const u = resolveHeaderUrl(communityLogoRaw);
    if (!u) return "";
    return `${u}${u.includes("?") ? "&" : "?"}v=${logoBust}`;
  }, [communityLogoRaw, logoBust]);

  // posts & search
  const channelPost = programChannelPost[channelIdentifier] || [];
  const { setSearchQuery, filteredResults } = useSearchPosts(channelPost);

  // Parse URL params for tab persistence
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlTab = searchParams.get('tab') as 'posts' | 'chats' | null;
  const urlChatId = searchParams.get('chatId');

  // Main tabs: Posts | Chats (initialize from URL if present)
  const [mainTab, setMainTab] = useState<'posts' | 'chats'>(urlTab || 'posts');
  // Sub-tabs within Posts: Recent | Pinned | About
  const [postsSubTab, setPostsSubTab] = useState(0);

  // About section collapse state (persisted globally via localStorage)
  const [aboutExpanded, setAboutExpanded] = useState(() => {
    const stored = localStorage.getItem('channel_about_expanded');
    return stored !== null ? stored === 'true' : true; // default expanded
  });

  // Persist about collapse state
  useEffect(() => {
    localStorage.setItem('channel_about_expanded', String(aboutExpanded));
  }, [aboutExpanded]);

  // Update URL when tab changes
  const handleMainTabChange = (newTab: 'posts' | 'chats') => {
    setMainTab(newTab);
    const newParams = new URLSearchParams(location.search);
    newParams.set('tab', newTab);
    // Clear chatId when switching to posts tab
    if (newTab === 'posts') {
      newParams.delete('chatId');
    }
    history.replace({ search: newParams.toString() });
  };

  // chatroom state
  const [chatrooms, setChatrooms] = useState<any[]>([]);
  const [chatroomsLoading, setChatroomsLoading] = useState(false);
  const chatroomsLoadAttempted = React.useRef<Set<string>>(new Set());

  // moderator dialog state
  const [modDialogOpen, setModDialogOpen] = useState(false);

  // form / modal state
  const initialFormId = (effectiveChannel as any)?.channelFormSettings?.forms?.[0]?.sourceFormId;
  const [selectedSourceFormId, setSelectedSourceFormId] = useState(initialFormId);

  // Update selectedSourceFormId when initialFormId changes (e.g., when forms load)
  // FIX: useState only initializes once at mount, before forms are loaded from API
  // This useEffect updates the state once forms become available
  useEffect(() => {
    if (initialFormId && initialFormId !== selectedSourceFormId) {
      setSelectedSourceFormId(initialFormId);
    }
  }, [initialFormId]);

  // fetch schema when modal opens
  useEffect(() => {
    if (formModal && selectedSourceFormId && programId && userId) {
      dispatch(getProgramFormStructure(programId, selectedSourceFormId, userId));
    }
  }, [dispatch, formModal, selectedSourceFormId, programId, userId]);

  // fetch channels if we don't have details yet
  useEffect(() => {
    if (!effectiveChannel && programId && userId && communityIdentifier) {
      dispatch(getEnrolledChannels(programId, userId, communityIdentifier));
    }
  }, [dispatch, effectiveChannel, programId, userId, communityIdentifier]);

  const visibleSubscriptions = useMemo(
    () =>
      (subscribeChannels || []).filter((item: any) => {
        const targetCommunity = item?.communityIdentifier || communityIdentifier || '';
        const targetChannel = item?.channelIdentifier || '';
        if (!targetChannel) return false;
        return privileges.canViewChannel(targetCommunity, targetChannel);
      }),
    [subscribeChannels, privileges, communityIdentifier]
  );

  // subscribe button state
  const [subscribed, setSubscribed] = useState(true);
  useEffect(() => {
    setSubscribed(
      !!visibleSubscriptions?.some((item) => item.channelIdentifier === channelIdentifier)
    );
  }, [channelIdentifier, visibleSubscriptions]);

  // toggle drawer
  useEffect(() => {
    dispatch(handleDrawer(!channelIdentifier));
  }, [dispatch, isMobile, channelIdentifier]);

  // Reset chatrooms when switching channels
  useEffect(() => {
    setChatrooms([]);
    setChatroomsLoading(false);
  }, [channelIdentifier]);

  // load chatrooms when Chats tab is selected
  useEffect(() => {
    // Only load if:
    // 1. Chats tab is active (mainTab === 'chats')
    // 2. We have a channel identifier
    // 3. We haven't already attempted to load for this channel
    if (mainTab === 'chats' && channelIdentifier && !chatroomsLoadAttempted.current.has(channelIdentifier)) {
      const loadChatrooms = async () => {
        setChatroomsLoading(true);
        // Mark as attempted immediately to prevent retries
        chatroomsLoadAttempted.current.add(channelIdentifier);

        try {
          // Pass communityIdentifier to filter chatrooms for this specific community
          // (avoids returning chatrooms from other communities with channels that have the same name)
          const result = await getChannelChatroomsApi(channelIdentifier, communityIdentifier);
          setChatrooms(result.data || []);
        } catch (error) {
          console.error('Error loading chatrooms:', error);
          // On error, set empty array to prevent loading spinner from showing indefinitely
          setChatrooms([]);
        } finally {
          setChatroomsLoading(false);
        }
      };
      loadChatrooms();
    }
  }, [mainTab, channelIdentifier, communityIdentifier]);

  // ========= Rich "About" content (only for About tab) =========
  const rawAbout: string = useMemo(() => {
    const ch: any = effectiveChannel || {};
    return ch.about ?? ch.channelDetails?.about ?? "";
  }, [effectiveChannel]);

  const aboutSanitized = useMemo(
    () => DOMPurify.sanitize(decodeHtml(rawAbout || ""), { USE_PROFILES: { html: true } }),
    [rawAbout]
  );

  // ========= Small plain description for the header section =====
  const smallDescription: string = useMemo(() => {
    const ch: any = effectiveChannel || {};
    return ch.channelDetails?.description ?? ch.description ?? "";
  }, [effectiveChannel]);

  /* ---------- THEMED CONTAINER STYLES (used even during load) ---------- */
  const containerStyle: React.CSSProperties = {
    backgroundColor: secondaryColor,             // ← always themed (channel → community → default)
    color: invertedSecondary,
    fontFamily: fontStyle,
    minHeight: "100vh",
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: secondaryColor,
    border: `1px solid ${invertedSecondary}22`,
    boxShadow: `0 4px 20px ${invertedSecondary}15`,
    borderRadius: "12px",
    overflow: "hidden",
  };

  const headerStyle: React.CSSProperties = {
    position: "relative",
    height: "200px",
    background: headerImageUrl
      ? `linear-gradient(135deg, ${mainColor}88, ${mainColor}44), url("${headerImageUrl}")`
      : `linear-gradient(135deg, ${mainColor}, ${secondaryColor === "#ffffff" ? "#e3f2fd" : mainColor}dd)`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    display: "flex",
    alignItems: "center",
    padding: "20px",
  };

  const headerOverlayStyle: React.CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, rgba(0,0,0,0.3), rgba(0,0,0,0.1))`,
  };

  const backButtonStyle: React.CSSProperties = {
    position: "absolute",
    top: "20px",
    left: "20px",
    background: "rgba(255,255,255,0.2)",
    border: "none",
    borderRadius: "8px",
    padding: "8px 12px",
    color: "white",
    cursor: "pointer",
    backdropFilter: "blur(10px)",
    display: "flex",
    alignItems: "center",
  };

  const channelBrandingStyle: React.CSSProperties = {
    position: "relative",
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    gap: "15px",
    marginLeft: "60px",
  };

  const channelIconStyle: React.CSSProperties = {
    width: "50px",
    height: "50px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "24px",
    fontWeight: 700,
    color: "white",
    background: "rgba(255,255,255,0.2)",
  };

  const channelTitleStyle: React.CSSProperties = {
    color: "white",
    fontSize: "28px",
    fontWeight: 700,
    textShadow: "0 2px 4px rgba(0,0,0,0.3)",
    margin: 0,
  };

  const aboutSectionStyle: React.CSSProperties = {
    padding: "25px",
    borderBottom: `1px solid ${invertedSecondary}22`,
  };

  const aboutTitleStyle: React.CSSProperties = {
    fontSize: "18px",
    fontWeight: 600,
    marginBottom: "10px",
    color: mainColor,
  };

  const aboutDescriptionStyle: React.CSSProperties = {
    color: invertedSecondary,
    lineHeight: 1.6,
    marginBottom: "15px",
    whiteSpace: "pre-wrap",
  };

  const channelStatsStyle: React.CSSProperties = {
    display: "flex",
    gap: "20px",
    fontSize: "14px",
    color: `${invertedSecondary}88`,
  };

  const actionsStyle: React.CSSProperties = {
    display: "flex",
    gap: "12px",
    alignItems: "center",
    marginBottom: "20px",
  };

  const buttonStyle: React.CSSProperties = {
    backgroundColor: mainColor,
    color: isHexColorLight(mainColor) ? "#000" : "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "12px 24px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    textTransform: "none",
  };

  const searchContainerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: "0 12px",
    border: `2px solid ${invertedSecondary}22`,
    borderRadius: "8px",
    backgroundColor: `${invertedSecondary}11`,
  };

  const searchIconStyle: React.CSSProperties = {
    marginRight: 8,
    color: `${invertedSecondary}66`,
  };

  const searchInputStyle: React.CSSProperties = {
    flex: 1,
    padding: "12px 0",
    color: invertedSecondary,
    fontSize: 14,
  };

  const postItemStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "15px",
    marginBottom: "10px",
    borderRadius: "8px",
    backgroundColor: `${invertedSecondary}08`,
    transition: "all 0.3s ease",
    cursor: "pointer",
    border: "none",
  };

  const postIconStyle: React.CSSProperties = {
    width: "32px",
    height: "32px",
    borderRadius: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
    color: "white",
    backgroundColor: mainColor,
  };

  const cps = (effectiveChannel as any)?.channelPostingSettings || {};
  const effectiveChannelId = (effectiveChannel as any)?.channelIdentifier;

  const channelRole = React.useMemo<'viewer' | 'member' | 'moderator' | 'admin'>(() => {
    if (!communityIdentifier || !effectiveChannelId) return 'viewer';
    return privileges.effectiveRoleForChannel(communityIdentifier, effectiveChannelId);
  }, [privileges, communityIdentifier, effectiveChannelId]);

  const canViewChannel = React.useMemo(() => {
    if (!communityIdentifier || !effectiveChannelId) return false;
    return privileges.canViewChannel(communityIdentifier, effectiveChannelId);
  }, [privileges, communityIdentifier, effectiveChannelId]);

  const canCreatePost = React.useMemo(() => {
    if (!communityIdentifier || !programId || !effectiveChannelId) {
      return false;
    }
    if (channelRole !== 'admin' && channelRole !== 'moderator') {
      return false;
    }
    return canPostToChannel({
      enrollments: privileges.activeEnrollments,
      ctx: { programId, communityIdentifier },
      channelIdentifier: effectiveChannelId,
      channelPostingSettings: cps,
    });
  }, [privileges.activeEnrollments, programId, communityIdentifier, effectiveChannelId, cps, channelRole]);

  /* ---------- THEMED LOADING STATE (no more white screen) ---------- */
  if (!effectiveChannel) {
    return (
      <div style={containerStyle}>
        <Box p={4}>
          <Typography style={{ color: invertedSecondary }}>Loading channel…</Typography>
        </Box>
      </div>
    );
  }

  if (!canViewChannel) {
    return (
      <div style={containerStyle}>
        <Box p={4}>
          <Typography style={{ color: invertedSecondary }}>
            You no longer have access to this channel.
          </Typography>
        </Box>
      </div>
    );
  }

  // safe reads
  const channelIcon = cps?.defaultIcon;
  const backRoute = location.pathname.includes("admin/channel") ? "/admin/dashboard" : "/user/dashboard";

  // actions
  const subscribeChannel = () =>
    dispatch(joinChannel(programId, auth?._id, communityIdentifier, channelIdentifier));
  const addPost = () => {
    dispatch(openProgramFormModal());
  };
  const handleCloseModal = () => {
    dispatch(closeProgramFormModal());
    dispatch(closeEnrollmentLoading());
  };

  return (
    <>
      <div style={containerStyle}>
        <MuiCard style={cardStyle}>
          {/* Enhanced Header */}
          <div key={headerImageUrl} style={headerStyle}>
            <div style={headerOverlayStyle}></div>
            <button
              style={backButtonStyle}
              onClick={() => {
                history.push(backRoute);
                if (isMobile) dispatch(handleDrawer(true));
                if (typeof handleBack === "function") handleBack();
              }}
            >
              <img
                src={backIcon}
                alt="Back"
                style={{
                  width: 20,
                  height: 20,
                  objectFit: "contain",
                  flexShrink: 0,
                  marginRight: 8,
                  filter: "brightness(0) invert(1)",
                }}
              />
              {/* 🔧 Prefer community logo; fall back to app logo */}
              <img
                src={communityLogoUrl || resolveHeaderUrl(appLogo)}
                alt="Logo"
                style={{ width: 72, height: 24, flexShrink: 0
                  //filter: "brightness(0) invert(1)" 
                }}
              />
            </button>
            <div style={channelBrandingStyle}>
              <div style={channelIconStyle}>
                <i className="material-icons" style={{ fontSize: "24px" }}>
                  {channelIcon || "forum"}
                </i>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Typography style={channelTitleStyle}>
                  {effectiveChannel?.channelName ||
                    (effectiveChannel as any)?.label ||
                    "Channel"}
                </Typography>
                <ChannelBellMenu
                  communityIdentifier={communityIdentifier}
                  channelIdentifier={channelIdentifier}
                  iconStyle={{ marginLeft: '4px' }}
                />
                {(channelRole === 'moderator' || channelRole === 'admin') && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setModDialogOpen(true);
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 10px',
                      backgroundColor: 'rgba(255,255,255,0.25)',
                      border: '1px solid rgba(255,255,255,0.4)',
                      borderRadius: '14px',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backdropFilter: 'blur(10px)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.35)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.25)';
                    }}
                    title={`Click to learn about ${channelRole} access`}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ marginRight: '4px' }}
                    >
                      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                    </svg>
                    {channelRole === 'admin' ? 'ADMIN' : 'MOD'}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* About Section (Collapsible) */}
          <div style={aboutSectionStyle}>
            <div
              onClick={() => setAboutExpanded(!aboutExpanded)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
              }}
            >
              <Typography style={aboutTitleStyle}>About This Channel</Typography>
              <IconButton size="small" style={{ color: mainColor }}>
                {aboutExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </div>

            <Collapse in={aboutExpanded}>
              {smallDescription?.trim() ? (
                <Typography style={aboutDescriptionStyle}>
                  {smallDescription}
                </Typography>
              ) : (
                <Typography style={aboutDescriptionStyle}>
                  {`Stay updated on ${(effectiveChannel as any)?.channelName || "this channel"}.`}
                </Typography>
              )}

              <div style={channelStatsStyle}>
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <span>📊</span>
                  <span>{filteredResults.length} Posts</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <span>👥</span>
                  <span>{visibleSubscriptions.length} Subscribers</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <span>📅</span>
                  <span>Updated Daily</span>
                </div>
              </div>
            </Collapse>
          </div>

          {/* Main Tabs: Posts | Chats (moved up, right after About) */}
          <Box px={3} pt={2} style={{ borderBottom: `2px solid ${invertedSecondary}22` }}>
            <Tabs
              value={mainTab === 'posts' ? 0 : 1}
              onChange={(_, v) => handleMainTabChange(v === 0 ? 'posts' : 'chats')}
              TabIndicatorProps={{ style: { backgroundColor: mainColor, height: 3, borderRadius: 2 } }}
            >
              <Tab
                label="Posts"
                style={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: '16px',
                  color: mainTab === 'posts' ? mainColor : `${invertedSecondary}88`,
                }}
              />
              <Tab
                label="Chats"
                style={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: '16px',
                  color: mainTab === 'chats' ? mainColor : `${invertedSecondary}88`,
                }}
              />
            </Tabs>
          </Box>

          {/* Channel Content */}
          <Box p={3}>

            {/* Posts Tab Content */}
            {mainTab === 'posts' && (
              <Box>
                {/* Actions - now inside Posts tab */}
                <div style={actionsStyle}>
                  {!subscribed && (
                    <Button variant="contained" onClick={subscribeChannel} style={buttonStyle}>
                      Subscribe
                    </Button>
                  )}
                  {canCreatePost && (
                    <Button variant="contained" onClick={addPost} style={buttonStyle}>
                      ADD POST
                    </Button>
                  )}
                </div>

                {/* Search - now inside Posts tab */}
                <Box mb={3}>
                  <Typography
                    style={{
                      fontSize: "16px",
                      fontWeight: 600,
                      marginBottom: "10px",
                      color: mainColor,
                    }}
                  >
                    Search posts
                  </Typography>
                  <div style={searchContainerStyle}>
                    <Search style={searchIconStyle} />
                    <InputBase
                      placeholder="Search..."
                      fullWidth
                      onChange={(e) => setSearchQuery(e.target.value)}
                      style={searchInputStyle}
                    />
                  </div>
                </Box>

                {/* Sub-tabs: Recent | Pinned | About */}
                <Box mb={2} style={{ borderBottom: `1px solid ${invertedSecondary}15` }}>
                  <Tabs
                    value={postsSubTab}
                    onChange={(_, v) => setPostsSubTab(v)}
                    TabIndicatorProps={{ style: { backgroundColor: mainColor, height: 2, borderRadius: 1 } }}
                  >
                    <Tab label="Recent" style={{ textTransform: "none", fontWeight: 500, fontSize: '14px', color: mainColor, minWidth: 80 }} />
                    <Tab label="Pinned" style={{ textTransform: "none", fontWeight: 500, fontSize: '14px', color: mainColor, minWidth: 80 }} />
                    <Tab label="About" style={{ textTransform: "none", fontWeight: 500, fontSize: '14px', color: mainColor, minWidth: 80 }} />
                  </Tabs>
                </Box>

                {/* Recent Posts (default) */}
                {postsSubTab === 0 && (
                  <Box>
                    <List style={{ padding: 0 }}>
                      {filteredResults.map((post: any, i: number) => {
                        const navigateToPost = () => {
                          console.log('[ChannelScreen] Navigating to post:', post?.contextId);
                          history.push(
                            `/user/post?contextId=${post?.contextId}&channelIdentifier=${post?.channelIdentifier}`
                          );
                        };

                        return (
                          <ListItem
                            button
                            key={post?.contextId || i}
                            style={{ ...postItemStyle, cursor: 'pointer' }}
                            onClick={navigateToPost}
                          >
                            <ListItemIcon style={{ minWidth: "auto" }}>
                              <div style={postIconStyle}>
                                <i className="material-icons" style={{ fontSize: "16px" }}>
                                  {channelIcon || post?.iconClass || "note"}
                                </i>
                              </div>
                            </ListItemIcon>
                            <ListItemText
                              primary={post?.data?.title}
                              secondary={post?.data?.message}
                              primaryTypographyProps={{
                                style: { color: invertedSecondary, fontWeight: 600, marginBottom: "4px" },
                              }}
                              secondaryTypographyProps={{
                                style: { color: `${invertedSecondary}88`, fontSize: "12px" },
                              }}
                              style={{ margin: 0 }}
                            />
                            <IconButton
                              edge="end"
                              style={{ color: mainColor, padding: "8px" }}
                              onClick={navigateToPost}
                            >
                              <LaunchIcon />
                            </IconButton>
                          </ListItem>
                        );
                      })}
                    </List>

                    {/* Optional empty-state content (keeps themed background visible) */}
                    {filteredResults.length === 0 && (
                      <Box py={6} textAlign="center">
                        <Typography style={{ color: `${invertedSecondary}cc`, fontWeight: 600 }}>
                          No posts yet
                        </Typography>
                        <Typography style={{ color: `${invertedSecondary}88`, marginTop: 6 }}>
                          When posts are added to this channel, you'll see them here.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Pinned tab (empty for now) */}
                {postsSubTab === 1 && (
                  <Box py={6} textAlign="center">
                    <Typography style={{ color: `${invertedSecondary}cc`, fontWeight: 600 }}>
                      No pinned posts
                    </Typography>
                    <Typography style={{ color: `${invertedSecondary}88`, marginTop: 6 }}>
                      Important posts will be pinned here.
                    </Typography>
                  </Box>
                )}

                {/* About tab — show sanitized rich HTML ONLY here */}
                {postsSubTab === 2 && (
                  <Box>
                    {aboutSanitized?.trim() ? (
                      <div
                        style={{ color: invertedSecondary, lineHeight: 1.6 }}
                        dangerouslySetInnerHTML={{ __html: aboutSanitized }}
                      />
                    ) : (
                      <Typography style={{ color: `${invertedSecondary}aa` }}>
                        No additional details yet.
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* Chats Tab Content - New Sidebar Layout */}
            {mainTab === 'chats' && (
              <ChannelChatsView
                channelIdentifier={channelIdentifier}
                communityIdentifier={communityIdentifier}
                programId={programId}
                userId={userId}
                userName={auth.firstName + ' ' + auth.lastName || 'User'}
                mainColor={mainColor}
                secondaryColor={secondaryColor}
                initialChatId={urlChatId || undefined}
                userRole={channelRole}
                onChatSelect={(chatId) => {
                  // Update URL with selected chatId
                  const newParams = new URLSearchParams(location.search);
                  if (chatId) {
                    newParams.set('chatId', chatId);
                  } else {
                    newParams.delete('chatId');
                  }
                  history.replace({ search: newParams.toString() });
                }}
              />
            )}
          </Box>
        </MuiCard>
      </div>

      {/* Moderator/Admin Info Dialog */}
      <Dialog
        open={modDialogOpen}
        onClose={() => setModDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{
          color: mainColor,
          fontFamily: fontStyle,
          fontWeight: 600,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
            </svg>
            {channelRole === 'admin' ? 'Administrator Access' : 'Moderator Access'}
          </div>
        </DialogTitle>
        <DialogContent>
          <div style={{ fontFamily: fontStyle, lineHeight: 1.6 }}>
            <p style={{ marginTop: 0 }}>
              You have <strong>{channelRole === 'admin' ? 'administrator' : 'moderator'} privileges</strong> for the <strong>{effectiveChannel?.channelName || (effectiveChannel as any)?.label || 'this channel'}</strong> channel.
            </p>
            <p>
              As {channelRole === 'admin' ? 'an administrator' : 'a moderator'}, you can:
            </p>
            <ul style={{ paddingLeft: '20px', margin: '12px 0' }}>
              <li>Manage and moderate posts in this channel</li>
              <li>Review and approve content</li>
              <li>Manage channel members and permissions</li>
              <li>Access special {channelRole === 'admin' ? 'administration' : 'moderation'} tools</li>
              {channelRole === 'admin' && (
                <>
                  <li>Configure channel settings and themes</li>
                  <li>Assign moderators to channels</li>
                </>
              )}
            </ul>
            <p style={{ marginBottom: 0, fontSize: '14px', color: '#666' }}>
              Use these privileges responsibly to maintain a healthy community.
            </p>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setModDialogOpen(false)}
            style={{
              color: mainColor,
              fontFamily: fontStyle,
              fontWeight: 600,
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      {formStructure && (
        <ActionItemFormModal
          isModal={formModal}
          closeModal={handleCloseModal}
          programId={programId}
          userId={userId}
          sourceFormId={
            selectedSourceFormId ||
            (effectiveChannel as any)?.channelFormSettings?.forms?.[0]?.sourceFormId ||
            ""
          }
          channelIdentifier={(effectiveChannel as any)?.channelIdentifier}
          communityIdentifier={communityIdentifier}
          contextId=""
          setSelectedSourceFormId={setSelectedSourceFormId}
          formSettings={(effectiveChannel as any)?.channelFormSettings?.forms || []}
        />
      )}
    </>
  );
}

export default ChannelDetail;
